/**
 * <CursorOverlay>: custom cursor with brush-preview circle.
 *
 * M2.T9.5 per IMPLEMENTATION_PLAN §13.15.
 *
 * - Brush: 1px crosshair + transparent-fill circle at current brush size,
 *   50% opacity stroke.
 * - Eraser: dashed circle (no crosshair).
 * - Hides the native OS cursor when active.
 *
 * Implemented as a positioned <div> overlay with a hidden SVG cursor
 * that tracks pointer move events via CSS custom properties.
 * This avoids re-rendering on every mouse move by updating CSS vars directly.
 */

import { DRAWING_WIDTH } from "./coordinates.js";
import { useLayoutEffect, useRef } from "preact/hooks";
import { useStore } from "@nanostores/preact";
import { $activeTool, $brushSize, BRUSH_SIZE_PX } from "../tools/toolStore.js";

const CROSSHAIR_SIZE = 12; // px, visual crosshair half-size

type CursorOverlayProps = {
  /** Element to attach pointer-move tracking to (the canvas parent). */
  targetRef: { current: HTMLElement | null };
};

/**
 * Renders a custom cursor layer over the drawing canvas.
 * Updates cursor position and shape in response to pointer events via
 * direct DOM style updates (no React re-render on every pointermove).
 */
export function CursorOverlay({ targetRef }: CursorOverlayProps) {
  const tool = useStore($activeTool);
  const sizeIdx = useStore($brushSize);
  const overlayRef = useRef<HTMLDivElement>(null);
  const displayScale = useRef(1);
  const brushRadius = BRUSH_SIZE_PX[sizeIdx] / 2;

  // Attach pointer move tracking via direct DOM updates for perf
  useLayoutEffect(() => {
    const target = targetRef.current;
    const overlay = overlayRef.current;
    if (!target || !overlay) return;

    const root = overlay.querySelector<SVGSVGElement>(".cursor-svg");

    // M5.T12: Apple Pencil 2 hover. A pointermove with
    // `pointerType === 'pen'` AND `buttons === 0` means the Pencil is
    // tracked above the screen but not yet touching. We expose that
    // state on the overlay via the `cursor-overlay--pen-hover` class so
    // the brush-preview circle can render as a softer "ghost" outline.
    // Mouse pointers always have buttons===0 when not pressed, so we
    // gate this on pointerType === 'pen' to avoid every mouse move
    // being treated as a hover state.
    function setPenHover(on: boolean): void {
      if (!overlay) return;
      const cls = "cursor-overlay--pen-hover";
      const has = overlay.classList.contains(cls);
      if (on && !has) overlay.classList.add(cls);
      else if (!on && has) overlay.classList.remove(cls);
    }

    function updateRadius() {
      const canvasWidth = target!.querySelector("canvas")?.getBoundingClientRect().width;
      displayScale.current = canvasWidth ? canvasWidth / DRAWING_WIDTH : 1;
      root?.querySelector(".cursor-circle")?.setAttribute("r", String(BRUSH_SIZE_PX[sizeIdx] * displayScale.current / 2));
    }
    updateRadius();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateRadius) : null;
    observer?.observe(target);
    const canvas = target.querySelector("canvas");
    if (canvas) observer?.observe(canvas);

    function onPointerMove(e: PointerEvent) {
      if (!root) return;
      const rect = target!.getBoundingClientRect();
      updateRadius();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      // Set the transform BEFORE flipping display, so the SVG never
      // renders at translate(0,0) when it appears.
      root.style.transform = `translate(${x}px, ${y}px)`;
      // Show on the first move too, not just on pointerenter. `pointerenter`
      // does not fire when the cursor is already inside the element at mount
      // time (e.g. splash→free-draw navigation, F5 refresh, tab switch
      // back). Without this, the wrapper's `cursor: none` + an unflipped
      // overlay leaves no visible cursor until the user crosses the
      // boundary manually.
      if (overlay!.style.display !== "block") overlay!.style.display = "block";
      setPenHover(e.pointerType === "pen" && e.buttons === 0);
    }

    function onPointerDown(e: PointerEvent) {
      // Touch-down ends the hover state regardless of pointer type.
      if (e.pointerType === "pen") setPenHover(false);
    }

    function onPointerEnter() {
      if (overlay) overlay.style.display = "block";
    }

    function onPointerLeave() {
      if (overlay) overlay.style.display = "none";
      setPenHover(false);
    }

    target.addEventListener("pointermove", onPointerMove as EventListener);
    target.addEventListener("pointerdown", onPointerDown as EventListener);
    target.addEventListener("pointerenter", onPointerEnter);
    target.addEventListener("pointerleave", onPointerLeave);

    return () => {
      observer?.disconnect();
      target.removeEventListener("pointermove", onPointerMove as EventListener);
      target.removeEventListener("pointerdown", onPointerDown as EventListener);
      target.removeEventListener("pointerenter", onPointerEnter);
      target.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [targetRef, sizeIdx]);

  const isEraser = tool === "eraser";
  const r = brushRadius * displayScale.current;
  const svgSize = Math.max(r * 2 + 4, CROSSHAIR_SIZE * 2 + 4);
  const cx = svgSize / 2;
  const cy = svgSize / 2;

  return (
    <div
      ref={overlayRef}
      class="cursor-overlay"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        display: "none",
        overflow: "hidden",
      }}
    >
      <svg
        class="cursor-svg"
        width={svgSize}
        height={svgSize}
        viewBox={`0 0 ${svgSize} ${svgSize}`}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          overflow: "visible",
          transform: "translate(0px, 0px)",
          // Center the SVG on the cursor position
          marginLeft: `-${cx}px`,
          marginTop: `-${cy}px`,
        }}
        aria-hidden="true"
      >
        {/* Brush-preview circle */}
        <circle
          class="cursor-circle"
          cx={cx}
          cy={cy}
          r={r}
          fill="transparent"
          stroke="rgba(0,0,0,0.5)"
          strokeWidth={1}
          strokeDasharray={isEraser ? "3 2" : undefined}
          data-eraser={isEraser ? "true" : undefined}
        />

        {/* Crosshair: only for brush tool */}
        {!isEraser && (
          <>
            <line
              class="cursor-cross-h"
              x1={cx - CROSSHAIR_SIZE}
              y1={cy}
              x2={cx + CROSSHAIR_SIZE}
              y2={cy}
              stroke="rgba(0,0,0,0.7)"
              strokeWidth={1}
            />
            <line
              class="cursor-cross-v"
              x1={cx}
              y1={cy - CROSSHAIR_SIZE}
              x2={cx}
              y2={cy + CROSSHAIR_SIZE}
              stroke="rgba(0,0,0,0.7)"
              strokeWidth={1}
            />
          </>
        )}
      </svg>
    </div>
  );
}
